(() => {
  const VISIBLE_LIMIT_DEFAULT = 13;
  const ALGOLIA_READY_ATTEMPTS = 40;
  const ALGOLIA_READY_DELAY_MS = 100;
  const COUNT_FALLBACK_MS = 8000;

  class AlgoliaCategoryNavigation extends HTMLElement {
    connectedCallback() {
      const parsedLimit = Number.parseInt(
        this.dataset.visibleLimit || String(VISIBLE_LIMIT_DEFAULT),
        10
      );
      this.#visibleLimit =
        Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : VISIBLE_LIMIT_DEFAULT;
      this.#headerButton = this.querySelector('.algolia-category-nav__header');
      this.#panel = this.querySelector('.algolia-category-nav__panel');
      this.#listToggle = this.querySelector('.algolia-category-nav__list-toggle');
      this.#items = [...this.querySelectorAll('.algolia-category-nav__item')];
      this.#headerExpanded = this.dataset.headerExpanded !== 'false';
      this.#listExpanded = false;

      this.#headerButton?.addEventListener('click', this.#toggleHeader);
      this.#listToggle?.addEventListener('click', this.#toggleList);

      this.#initListTruncation();
      this.#syncHeaderState();
      this.#syncAlgoliaCounts();
    }

    disconnectedCallback() {
      this.#headerButton?.removeEventListener('click', this.#toggleHeader);
      this.#listToggle?.removeEventListener('click', this.#toggleList);

      if (this.#countFallbackTimer) {
        clearTimeout(this.#countFallbackTimer);
        this.#countFallbackTimer = null;
      }
    }

    /** @type {number} */
    #visibleLimit;

    /** @type {HTMLButtonElement | null} */
    #headerButton;

    /** @type {HTMLElement | null} */
    #panel;

    /** @type {HTMLButtonElement | null} */
    #listToggle;

    /** @type {HTMLElement[]} */
    #items;

    /** @type {boolean} */
    #headerExpanded;

    /** @type {boolean} */
    #listExpanded;

    /** @type {ReturnType<typeof setTimeout> | null} */
    #countFallbackTimer = null;

    #toggleHeader = () => {
      this.#headerExpanded = !this.#headerExpanded;
      this.#syncHeaderState();
    };

    #toggleList = () => {
      this.#listExpanded = !this.#listExpanded;
      this.#updateListVisibility();
    };

    #syncHeaderState() {
      if (!this.#headerButton || !this.#panel) return;

      this.#panel.hidden = !this.#headerExpanded;
      this.#headerButton.setAttribute('aria-expanded', String(this.#headerExpanded));
      this.dataset.headerExpanded = String(this.#headerExpanded);
      this.classList.toggle('algolia-category-nav--header-collapsed', !this.#headerExpanded);
    }

    #initListTruncation() {
      if (!this.#listToggle) return;
      this.#updateListVisibility();
    }

    #getListItems() {
      return this.#items.filter((item) => !item.hasAttribute('data-empty-collection'));
    }

    #updateListVisibility() {
      const items = this.#getListItems();
      const fadeIndex = this.#visibleLimit - 1;
      const truncate = !this.#listExpanded && items.length > this.#visibleLimit;

      items.forEach((item, index) => {
        item.classList.remove('algolia-category-nav__item--fade');

        if (truncate && index >= this.#visibleLimit) {
          item.hidden = true;
          return;
        }

        item.hidden = false;

        if (truncate && index === fadeIndex) {
          item.classList.add('algolia-category-nav__item--fade');
        }
      });

      const showMoreLabel = this.querySelector('.algolia-category-nav__list-toggle-label--more');
      const showLessLabel = this.querySelector('.algolia-category-nav__list-toggle-label--less');

      if (showMoreLabel) showMoreLabel.hidden = this.#listExpanded;
      if (showLessLabel) showLessLabel.hidden = !this.#listExpanded;

      if (this.#listToggle) {
        this.#listToggle.hidden = items.length <= this.#visibleLimit;
        this.#listToggle.setAttribute('aria-expanded', String(this.#listExpanded));
      }
    }

    async #syncAlgoliaCounts() {
      const pendingCounts = [
        ...this.querySelectorAll('.algolia-category-nav__count[data-algolia-count-pending="true"]'),
      ];
      if (!pendingCounts.length) return;

      this.#scheduleCountFallback(pendingCounts);

      const items = this.#items.filter((item) => item.dataset.collectionId);
      const ready = await this.#waitForAlgolia();

      if (!ready) {
        this.#revealLiquidCounts(pendingCounts);
        return;
      }

      const { appId, apiKey, indexName } = window.algoliaConfig || {};
      if (!appId || !apiKey || !indexName || !items.length) {
        this.#revealLiquidCounts(pendingCounts);
        return;
      }

      try {
        const client = window.algoliasearch.algoliasearch(appId, apiKey);
        const requests = items.map((item) => ({
          indexName,
          params: {
            query: '',
            hitsPerPage: 0,
            filters: `collection_ids:${item.dataset.collectionId}`,
            analytics: true,
            clickAnalytics: true,
            analyticsTags: ['category-browsing'],
          },
        }));

        const response = await this.#runSearch(client, requests);
        const results = response?.results;

        if (!Array.isArray(results)) {
          this.#revealLiquidCounts(pendingCounts);
          return;
        }

        const syncedItems = new Set();

        results.forEach((result, index) => {
          const item = items[index];
          if (!item || typeof result?.nbHits !== 'number') return;

          this.#updateItemCount(item, result.nbHits);
          syncedItems.add(item);
        });

        const unresolved = pendingCounts.filter((countEl) => {
          const item = countEl.closest('.algolia-category-nav__item');
          return !item || !syncedItems.has(item);
        });

        if (unresolved.length) {
          this.#revealLiquidCounts(unresolved);
        }

        this.#initListTruncation();
      } catch (error) {
        console.warn('Algolia category nav: failed to sync collection counts', error);
        this.#revealLiquidCounts(pendingCounts);
      }
    }

    async #runSearch(client, requests) {
      try {
        return await client.search(requests);
      } catch (error) {
        return client.search({ requests });
      }
    }

    #scheduleCountFallback(pendingCounts) {
      if (this.#countFallbackTimer) {
        clearTimeout(this.#countFallbackTimer);
      }

      this.#countFallbackTimer = setTimeout(() => {
        const stillPending = pendingCounts.filter(
          (countEl) => countEl.getAttribute('data-algolia-count-pending') === 'true'
        );
        if (stillPending.length) {
          this.#revealLiquidCounts(stillPending);
        }
      }, COUNT_FALLBACK_MS);
    }

    #hideEmptyItem(item) {
      item.hidden = true;
      item.setAttribute('data-empty-collection', 'true');
      item.setAttribute('aria-hidden', 'true');
    }

    #updateItemCount(item, count) {
      if (count === 0) {
        this.#hideEmptyItem(item);
        return;
      }

      let countEl = item.querySelector('.algolia-category-nav__count');

      if (!countEl) {
        const link = item.querySelector('.algolia-category-nav__link');
        if (!link) return;

        countEl = document.createElement('span');
        countEl.className = 'algolia-category-nav__count';
        link.appendChild(countEl);
      }

      countEl.textContent = `(${count})`;
      countEl.hidden = false;
      countEl.removeAttribute('data-algolia-count-pending');
      countEl.dataset.algoliaSynced = 'true';
    }

    #revealLiquidCounts(countElements) {
      countElements.forEach((countEl) => {
        if (countEl.getAttribute('data-algolia-count-pending') !== 'true') return;

        const liquidCount = countEl.dataset.liquidCount;
        if (liquidCount === undefined || liquidCount === '') return;

        const item = countEl.closest('.algolia-category-nav__item');
        const count = Number.parseInt(liquidCount, 10);

        if (item && count === 0) {
          this.#hideEmptyItem(item);
          countEl.removeAttribute('data-algolia-count-pending');
          return;
        }

        countEl.textContent = `(${liquidCount})`;
        countEl.hidden = false;
        countEl.removeAttribute('data-algolia-count-pending');
      });

      this.#initListTruncation();
    }

    #waitForAlgolia() {
      // UZ-8543: request the libraries in case this block sits outside a collection/search page
      window.loadAlgoliaLibs?.().catch(() => {});
      return new Promise((resolve) => {
        let attempts = 0;

        const check = () => {
          if (window.algoliasearch?.algoliasearch && window.algoliaConfig?.indexName) {
            resolve(true);
            return;
          }

          attempts += 1;
          if (attempts >= ALGOLIA_READY_ATTEMPTS) {
            resolve(false);
            return;
          }

          setTimeout(check, ALGOLIA_READY_DELAY_MS);
        };

        check();
      });
    }
  }

  if (!customElements.get('algolia-category-navigation')) {
    customElements.define('algolia-category-navigation', AlgoliaCategoryNavigation);
  }
})();
